import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

export default function BecomeHost() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: '',
    experience: '',
    why_host: '',
    social_links: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.why_host) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.applyForHost(formData);
      toast.success('Application submitted successfully! We will review it shortly.');
      navigate('/profile');
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
            Become a Host
          </h1>
          <p className="text-lg text-gray-400 mt-2">
            Create and manage your own events on Vibezlink
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="glass border-white/10 border-primary/50">
        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            <Briefcase className="h-8 w-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-heading font-bold mb-2">Why become a host?</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>• Create and manage unlimited events</li>
                <li>• Access powerful analytics and insights</li>
                <li>• Reach thousands of potential attendees</li>
                <li>• Scan tickets and manage check-ins</li>
                <li>• Build your brand and community</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Application Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Host Application</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business/Organization Name *</Label>
              <Input
                id="business_name"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="Your company or organization name"
                className="glass border-white/10"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_type">Business Type</Label>
              <Input
                id="business_type"
                value={formData.business_type}
                onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                placeholder="e.g., Event management, Nightclub, Festival organizer"
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="experience">Event Hosting Experience</Label>
              <Textarea
                id="experience"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                placeholder="Tell us about your experience organizing events..."
                className="glass border-white/10 min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="why_host">Why do you want to become a host? *</Label>
              <Textarea
                id="why_host"
                value={formData.why_host}
                onChange={(e) => setFormData({ ...formData, why_host: e.target.value })}
                placeholder="Share your motivation and goals..."
                className="glass border-white/10 min-h-[120px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="social_links">Social Media Links (Optional)</Label>
              <Textarea
                id="social_links"
                value={formData.social_links}
                onChange={(e) => setFormData({ ...formData, social_links: e.target.value })}
                placeholder="Instagram, Twitter, Website, etc. (one per line)"
                className="glass border-white/10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex space-x-4">
          <Button
            type="button"
            onClick={() => navigate('/profile')}
            variant="outline"
            className="flex-1 glass-hover"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 shadow-glow"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </div>
      </form>
    </div>
  );
}
